import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { AuthShell, authInputClass } from '../lib/authUi';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { data: signInData, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }
    const userId = signInData.user.id;
    const { data: internal } = await supabase.from('internal_users').select('is_active').eq('id', userId).maybeSingle();
    let isActive = internal?.is_active;
    let supplierRecord = null;
    if (isActive === undefined || isActive === null) {
      const { data: supplier } = await supabase.from('supplier_users').select('is_active, supplier_id').eq('id', userId).maybeSingle();
      isActive = supplier?.is_active;
      supplierRecord = supplier;
    }
    if (isActive === false) {
      await supabase.auth.signOut();
      setError('Your account has been disabled. Please contact a Supply Pilot administrator.');
      setLoading(false);
      return;
    }
    // Log supplier sign-ins so the Activity tab on the supplier card can
    // show a history (Supabase Auth only exposes the most recent
    // last_sign_in_at). Internal user sign-ins aren't logged here — there's
    // no equivalent surface for them yet, and the column would be unused.
    // Fire-and-forget: never block the redirect if the insert fails.
    if (supplierRecord?.supplier_id) {
      supabase.from('supplier_login_events').insert([{
        supplier_id: supplierRecord.supplier_id,
        supplier_user_id: userId,
        user_email: signInData.user.email || email,
      }]).then(() => {});
    }
    // Fresh login → show the Otto intro banner again (it's dismissible
    // per session; clearing the flag here makes it return each login).
    try { sessionStorage.removeItem('otto_intro_dismissed'); } catch (e) { /* ignore */ }
    // Re-arm Otto's Pinned-page hello so it plays once each fresh login.
    try { sessionStorage.removeItem('otto_pinned_hello_v2'); } catch (e) { /* ignore */ }
    window.location.href = '/dashboard';
  };

  return (
    <AuthShell>
      {/* Card tokenised to the Fusion login standard: bg #18181A,
          1px rgba(255,255,255,0.06) border, 16px radius, 430px max,
          40/40/36 padding. Frame (bokeh, logo, confidential line) is
          the shared AuthShell so it stays identical across the family. */}
      <form onSubmit={handleLogin} className="relative w-full max-w-[430px]">
        <div className="bg-[#18181A] border border-white/[0.06] rounded-[16px] shadow-2xl px-10 pt-10 pb-9">

          {/* Heading block — the only per-app variation (guideline §6).
              Rhythm: eyebrow →8→ title →12→ descriptor →16→ subtitle →28. */}
          <div className="text-center mb-7">
            <p className="font-['Metropolis'] text-[13px] font-semibold tracking-[0.22em] uppercase text-[#92D400] mb-2">
              Fusion 2K
            </p>
            <h1 className="font-['Metropolis'] text-[34px] font-extrabold tracking-[0.04em] uppercase text-white leading-none">
              Supply Pilot
            </h1>
            <p className="font-['Metropolis'] text-xs font-semibold tracking-[0.18em] uppercase text-[#92D400] mt-3">
              Fusion 2K's Supply &amp; Logistics Manager
            </p>
            <p className="font-['Metropolis'] text-[15px] text-[#9A9AA0] mt-4 leading-relaxed">
              Please sign in with your email address and password to continue
            </p>
          </div>

          <div className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Your email"
              className={authInputClass}
            />

            <div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Password"
                  className={`${authInputClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#6B6B72] hover:text-[#1A1A1A] focus:outline-none focus-visible:text-[#1A1A1A] transition-colors"
                >
                  {showPassword ? <EyeOff size={18} strokeWidth={1.9} /> : <Eye size={18} strokeWidth={1.9} />}
                </button>
              </div>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="block ml-auto mt-2 font-['Metropolis'] text-[13px] font-medium text-[#9A9AA0] hover:text-[#92D400] transition-colors"
              >
                Forgot your password?
              </button>
            </div>

            {error && (
              <div className="bg-[#E0290A]/10 border border-[#E0290A]/30 px-4 py-3 rounded-xl">
                <p className="font-['Metropolis'] text-sm text-[#E0290A]">{error}</p>
              </div>
            )}
          </div>

          {/* Canonical filled button (guideline §7) — inside the card,
              28px below the fields. 52px, radius 10, 1px card border,
              green 700 uppercase 0.14em on --button-bg with hover lift. */}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-[52px] mt-7 rounded-[10px] bg-[#232326] hover:bg-[#2C2C30] border border-white/[0.06] font-['Metropolis'] text-[15px] font-bold uppercase tracking-[0.14em] text-[#92D400] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-fusion-green"
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </div>
      </form>
    </AuthShell>
  );
}
